import { AtlassianNavigation } from '@atlaskit/atlassian-navigation';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { useCreateTeamModal } from '@townsquare/create-team-modal';
import { GiveFeedbackButton } from '@townsquare/feedback/button';
import { useInviteUserModal } from '@townsquare/invite-user-modal';
import { ProfileAndWorkspaceSwitcherButton } from '@townsquare/navigation';
import { CrossProductSearch } from '@townsquare/navigation/cross-product-search';
import { HelpPanel } from '@townsquare/navigation/help';
import { NewButton } from '@townsquare/navigation/new-button';
import { NotificationsBell } from '@townsquare/navigation/notifications';
import { usePrimaryNavItems } from '@townsquare/navigation/primaryNavigationItems';
import { AccountPopup } from '@townsquare/navigation/profile';
import { SettingsPopup } from '@townsquare/navigation/settings-home';
import { AppSwitcherButton } from '@townsquare/navigation/switcher-button';
import { teamProfileRoute } from '@townsquare/ptc-directory-view';
import { MOBILE_MODE_WIDTH_THRESHOLD } from '@townsquare/responsive-grid';
import { useRouterActions } from '@townsquare/router/primitives';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

import { ProductHome } from './ProductHome';

const newButtonSpacingStyles = xcss({ marginInlineStart: 'space.150' });

export const ChromeNavigation = () => {
  const intl = useIntl();
  const noWorkspaceExperience = useNoWorkspaceExperience();
  const { pushTo } = useRouterActions();
  const [isMobileWidth, setIsMobileWidth] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const [createTeamModal, createTeamModalTrigger] = useCreateTeamModal({
    analyticsCtx: { source: 'teamsNav' },
    onCreate: team => {
      pushTo(teamProfileRoute, { params: { id: team.id } });
    },
  });

  const [inviteUserModal, inviteUserModalTrigger] = useInviteUserModal({
    analyticsCtx: { source: 'teamsNav' },
  });

  const navItems = usePrimaryNavItems({
    triggers: { createTeamModalTrigger, inviteUserModalTrigger },
    isHome: true,
  });

  // useRef prevents this from being recreated on each render
  const resizeObserver = useRef(
    new ResizeObserver(entries => {
      if (entries[0].contentRect.width <= MOBILE_MODE_WIDTH_THRESHOLD) {
        setIsMobileWidth(true);
      } else {
        setIsMobileWidth(false);
      }
    }),
  );

  useEffect(() => {
    if (containerRef?.current) {
      resizeObserver.current.observe(containerRef.current);
    } else {
      resizeObserver.current.disconnect();
    }
  }, [containerRef, resizeObserver]);

  const hideIfOnMobile = (renderContents: undefined | (() => JSX.Element)) => {
    if (isMobileWidth) {
      return undefined;
    }

    return renderContents;
  };

  const renderNewButton = useCallback(
    () => (
      <Flex alignItems="center" xcss={newButtonSpacingStyles}>
        {noWorkspaceExperience ? null : <NewButton />}
        {!isMobileWidth && <GiveFeedbackButton />}
      </Flex>
    ),
    [noWorkspaceExperience, isMobileWidth],
  );

  return (
    <div ref={containerRef}>
      <Box>
        <AtlassianNavigation
          renderAppSwitcher={hideIfOnMobile(AppSwitcherButton)}
          primaryItems={navItems}
          renderCreate={renderNewButton}
          renderProductHome={ProductHome}
          renderSearch={CrossProductSearch}
          renderProfile={noWorkspaceExperience ? AccountPopup : ProfileAndWorkspaceSwitcherButton}
          renderNotifications={NotificationsBell}
          renderSettings={SettingsPopup}
          renderHelp={() => <HelpPanel isMobileWidth={isMobileWidth} />}
          moreLabel={isMobileWidth ? 'Menu' : 'More'}
          label={intl.formatMessage({
            id: 'townsquare.navigation.chrome-navigation.label',
            description:
              'Describes the specific role of this navigation component for users viewing the page with a screen reader',
            defaultMessage: 'top',
          })}
          testId="atlas-navigation"
        />
      </Box>
      {createTeamModal}
      {inviteUserModal}
    </div>
  );
};

export { ChromeNavigation as Navigation };
